import React, { useState, useEffect } from 'react';
import { Lock, Check, X, Sparkles } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function PassphraseBuilder() {
  const [passphrase, setPassphrase] = useState('');
  const [strength, setStrength] = useState({ score: 0, label: '', color: '' });

  useEffect(() => {
    const calculateStrength = (pass) => {
      let score = 0;
      const checks = {
        length12: pass.length >= 12,
        hasUppercase: /[A-Z]/.test(pass),
        hasLowercase: /[a-z]/.test(pass),
        hasNumbers: /\d/.test(pass),
        hasSymbols: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass),
      };

      if (checks.length12) score += 25;
      if (checks.hasUppercase) score += 15;
      if (checks.hasLowercase) score += 15;
      if (checks.hasNumbers) score += 20;
      if (checks.hasSymbols) score += 25;

      let label = '';
      let color = '';

      if (score === 0) {
        label = 'Enter a passphrase';
        color = 'stone';
      } else if (score < 40) {
        label = 'Weak';
        color = 'error';
      } else if (score < 70) {
        label = 'Medium';
        color = 'warning';
      } else if (score < 100) {
        label = 'Strong';
        color = 'primary';
      } else {
        label = 'Excellent!';
        color = 'success';
      }

      return { score, label, color, checks };
    };

    setStrength(calculateStrength(passphrase));
  }, [passphrase]);

  const examplePhrases = [
    'BlueKoala!Summer$Tree',
    'PurpleSunset@Beach2024',
    'HappyDog&Runs#Fast',
    'MyGarden*Grows!Roses',
  ];

  const generateRandom = () => {
    const words = ['Happy', 'Blue', 'Sunny', 'Quick', 'Bright', 'Golden', 'Silent', 'Purple'];
    const nouns = ['Koala', 'River', 'Mountain', 'Garden', 'Beach', 'Forest', 'Ocean', 'Sunset'];
    const symbols = ['!', '@', '#', '$', '&', '*'];
    const numbers = ['2024', '42', '365', '99'];

    const word1 = words[Math.floor(Math.random() * words.length)];
    const noun1 = nouns[Math.floor(Math.random() * nouns.length)];
    const symbol = symbols[Math.floor(Math.random() * symbols.length)];
    const word2 = words[Math.floor(Math.random() * words.length)];
    const noun2 = nouns[Math.floor(Math.random() * nouns.length)];
    const number = numbers[Math.floor(Math.random() * numbers.length)];

    setPassphrase(`${word1}${noun1}${symbol}${word2}${noun2}${number}`);
  };

  return (
    <div 
      className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl border-2 border-primary/20 p-6 md:p-8"
      data-testid="passphrase-builder"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Lock className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-text mb-1">Build Your Passphrase</h3>
          <p className="text-text-muted text-sm">
            Create a strong passphrase using words, numbers, and symbols
          </p>
        </div>
      </div>

      <div className="mb-6">
        <Input
          data-testid="passphrase-input"
          type="text"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder="Try typing: BlueKoala!Summer$Tree"
          className="h-16 px-6 rounded-xl border-2 border-stone-300 focus:border-primary text-xl font-mono bg-white"
        />
      </div>

      {/* Strength meter */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-text-muted">Strength</span>
          <span className={cn(
            "text-sm font-bold",
            strength.color === 'error' && "text-error",
            strength.color === 'warning' && "text-warning",
            strength.color === 'primary' && "text-primary",
            strength.color === 'success' && "text-success",
            strength.color === 'stone' && "text-stone-400"
          )}>
            {strength.label}
          </span>
        </div>
        <div className="h-4 bg-white rounded-full overflow-hidden border border-stone-200">
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500",
              strength.color === 'error' && "bg-error",
              strength.color === 'warning' && "bg-warning",
              strength.color === 'primary' && "bg-primary",
              strength.color === 'success' && "bg-success"
            )}
            style={{ width: `${strength.score}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      {passphrase.length > 0 && strength.checks && (
        <div className="bg-white rounded-xl p-4 mb-6 space-y-2">
          <p className="text-sm font-medium text-text mb-3">Requirements:</p>
          {[
            { key: 'length12', label: 'At least 12 characters' },
            { key: 'hasUppercase', label: 'Contains uppercase letter' },
            { key: 'hasLowercase', label: 'Contains lowercase letter' },
            { key: 'hasNumbers', label: 'Contains a number' },
            { key: 'hasSymbols', label: 'Contains a symbol (!@#$%)' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              {strength.checks[key] ? (
                <Check className="w-4 h-4 text-success" />
              ) : (
                <X className="w-4 h-4 text-stone-300" />
              )}
              <span className={strength.checks[key] ? "text-success" : "text-text-muted"}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Generate button */}
      <Button
        data-testid="generate-passphrase"
        onClick={generateRandom}
        variant="outline"
        className="w-full rounded-xl py-4 font-semibold mb-4"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        Generate Random Passphrase
      </Button>

      {/* Examples */}
      <div className="bg-white/50 rounded-xl p-4">
        <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
          Example strong passphrases:
        </p>
        <div className="flex flex-wrap gap-2">
          {examplePhrases.map((phrase, index) => (
            <button
              key={index}
              onClick={() => setPassphrase(phrase)}
              className="text-xs bg-white px-3 py-1.5 rounded-full border border-stone-200 hover:border-primary transition-colors font-mono"
            >
              {phrase}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
